import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDate } from '../regureStyles';

const STATUS_LABEL_KEYS = {
  active: 'screens.policiesScreen.statusActive',
  bound: 'screens.policiesScreen.statusBound',
  pending: 'screens.policiesScreen.statusPending',
  expired: 'screens.policiesScreen.statusExpired',
  cancelled: 'screens.policiesScreen.statusCancelled',
  lapsed: 'screens.policiesScreen.statusLapsed',
};

// Statuses the tester can transition a policy through in-place. Excludes
// 'lapsed' (that's an automated state) and 'expired' (derived from dates).
const EDITABLE_STATUSES = ['active', 'bound', 'pending', 'cancelled'];

const Header = ({ history, policyNumber, holderName, status, statusCfg, policy, S, lob, annualPremium, onPatch }) => {
  const { t } = useTranslation();
  const statusLabel = STATUS_LABEL_KEYS[status] ? t(STATUS_LABEL_KEYS[status]) : (status.charAt(0).toUpperCase() + status.slice(1));
  // D-21: no edit affordance existed. Inline-edit the most-commonly
  // amended fields (status, start/end date, annualPremium) via
  // onPatch (wired from PolicyDetailScreen → patchPolicy in the hook).
  // Full restructure/product-change should still go through the
  // Convert-to-Policy flow from an offer, so this is deliberately
  // scoped small.
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [errEdit, setErrEdit] = useState('');
  const [fStatus, setFStatus]     = useState(status);
  const [fStart,  setFStart]      = useState(policy?.startDate ? String(policy.startDate).slice(0, 10) : '');
  const [fEnd,    setFEnd]        = useState(policy?.endDate ? String(policy.endDate).slice(0, 10) : '');
  const [fPremium, setFPremium]   = useState(annualPremium ?? '');
  const cancelEdit = () => {
    setEditing(false);
    setErrEdit('');
    setFStatus(status);
    setFStart(policy?.startDate ? String(policy.startDate).slice(0, 10) : '');
    setFEnd(policy?.endDate ? String(policy.endDate).slice(0, 10) : '');
    setFPremium(annualPremium ?? '');
  };
  const saveEdit = async () => {
    if (!onPatch) return;
    setSavingEdit(true);
    setErrEdit('');
    const patch = {
      status: fStatus,
      startDate: fStart || null,
      endDate: fEnd || null,
      annualPremium: fPremium === '' ? null : Number(fPremium),
    };
    const res = await onPatch(patch);
    if (res.ok) setEditing(false);
    else setErrEdit(res.error || t('screens.policiesScreen.detail.updateFailed', { defaultValue: 'Update failed' }));
    setSavingEdit(false);
  };
  return (
    <header
      style={{ padding: '20px 32px', borderBottom: `1px solid ${S.borderLight}`, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(20px)', flexShrink: 0 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={() => history.push('/policies')}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 34,
              height: 34,
              borderRadius: 8,
              border: `1px solid ${S.borderLight}`,
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke={S.textSecondary} strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='19' y1='12' x2='5' y2='12' />
              <polyline points='12 19 5 12 12 5' />
            </svg>
          </button>
          <div>
            <div style={{ fontSize: 12, color: S.textMuted, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              {t('screens.policiesScreen.operations')}
              <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke={S.textMuted} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='9 18 15 12 9 6' />
              </svg>
              {t('screens.policiesScreen.breadcrumbPolicies')}
              <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke={S.textMuted} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='9 18 15 12 9 6' />
              </svg>
              <span style={{ color: S.textPrimary, fontWeight: 500 }}>{policyNumber}</span>
            </div>
            {/* D-23: client name → link to client detail. Was plain text,
                the tester expected it to navigate. Retest 2026-08-04: the
                first fix only checked clientRef/clientId, but the Policy
                model actually stores the ref as `client` (ObjectId to
                Client) — every policy fell through to plain text. Accept
                all three keys and support populated {_id} shape. */}
            {(() => {
              const rawRef = policy.clientRef || policy.clientId || policy.client;
              const clientId = rawRef && typeof rawRef === 'object' ? rawRef._id : rawRef;
              return clientId ? (
                <button
                  type='button'
                  onClick={() => history.push(`/clients/${clientId}`)}
                  style={{ fontSize: 20, fontWeight: 700, color: S.burgundy, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {holderName}
                </button>
              ) : (
                <div style={{ fontSize: 20, fontWeight: 700, color: S.textPrimary }}>{holderName}</div>
              );
            })()}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 12px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: 600,
              background: statusCfg.bg,
              color: statusCfg.color,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusCfg.color }} />
            {statusLabel}
          </span>
          {onPatch && !editing && (
            <>
              <button
                type='button'
                onClick={() => setEditing(true)}
                style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${S.borderLight}`, background: '#fff', color: S.textPrimary, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('screens.policiesScreen.detail.edit', { defaultValue: 'Edit' })}
              </button>
              {/* L-12: Cancel / Mark as Lapsed. Prompt for the reason
                  (recorded on Policy.cancellationReason). Only rendered
                  when the current status is active/bound/pending so the
                  actions don't re-fire on an already-terminated policy. */}
              {['active', 'bound', 'pending'].includes(status) && (
                <>
                  <button
                    type='button'
                    onClick={async () => {
                      const reason = window.prompt(t('screens.policiesScreen.detail.cancelPrompt', { defaultValue: 'Reason for cancellation:' }));
                      if (reason === null) return;
                      const r = await onPatch({ status: 'cancelled', cancellationReason: reason || t('screens.policiesScreen.detail.cancelDefaultReason', { defaultValue: 'Cancelled without reason' }) });
                      if (!r.ok) setErrEdit(r.error || t('screens.policiesScreen.detail.updateFailed', { defaultValue: 'Update failed' }));
                    }}
                    style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${S.borderLight}`, background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {t('screens.policiesScreen.detail.cancelPolicy', { defaultValue: 'Cancel Policy' })}
                  </button>
                  <button
                    type='button'
                    onClick={async () => {
                      const reason = window.prompt(t('screens.policiesScreen.detail.lapsePrompt', { defaultValue: 'Reason for lapse:' }));
                      if (reason === null) return;
                      const r = await onPatch({ status: 'expired', cancellationReason: reason || t('screens.policiesScreen.detail.lapseDefaultReason', { defaultValue: 'Lapsed without reason' }) });
                      if (!r.ok) setErrEdit(r.error || t('screens.policiesScreen.detail.updateFailed', { defaultValue: 'Update failed' }));
                    }}
                    style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${S.borderLight}`, background: '#fff', color: '#a16207', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {t('screens.policiesScreen.detail.lapsePolicy', { defaultValue: 'Mark as Lapsed' })}
                  </button>
                </>
              )}
            </>
          )}
          {editing && (
            <>
              <button type='button' onClick={cancelEdit} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${S.borderLight}`, background: '#fff', color: S.textSecondary, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                {t('screens.policiesScreen.detail.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button type='button' onClick={saveEdit} disabled={savingEdit} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#800020', color: '#fff', fontSize: 13, fontWeight: 600, cursor: savingEdit ? 'wait' : 'pointer' }}>
                {savingEdit ? t('screens.policiesScreen.detail.saving', { defaultValue: 'Saving…' }) : t('screens.policiesScreen.detail.save', { defaultValue: 'Save' })}
              </button>
            </>
          )}
        </div>
      </div>

      {errEdit && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{errEdit}</div>
      )}

      {/* Summary */}
      <div
        style={{
          background: S.bgCard,
          border: `1px solid ${S.borderLight}`,
          borderRadius: 14,
          padding: '20px 24px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr 1fr',
          gap: 20,
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('screens.policiesScreen.detail.colPolicyNumber')}</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: S.burgundy }}>{policyNumber}</div>
          <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{lob}</div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('screens.policiesScreen.detail.colPeriod')}</div>
          {editing ? (
            <div style={{ display: 'flex', gap: 6, flexDirection: 'column' }}>
              <input type='date' value={fStart} onChange={e => setFStart(e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${S.borderLight}`, borderRadius: 4, fontSize: 13 }} />
              <input type='date' value={fEnd}   onChange={e => setFEnd(e.target.value)}   style={{ padding: '4px 8px', border: `1px solid ${S.borderLight}`, borderRadius: 4, fontSize: 13 }} />
            </div>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{policy.startDate ? formatDate(policy.startDate) : '—'}</div>
              <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('screens.policiesScreen.detail.periodTo', { date: policy.endDate ? formatDate(policy.endDate) : '—' })}</div>
            </>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('screens.policiesScreen.detail.colAnnualPremium')}</div>
          {editing ? (
            <input type='number' step='0.01' value={fPremium} onChange={e => setFPremium(e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${S.borderLight}`, borderRadius: 4, fontSize: 13, width: '100%' }} />
          ) : (
            <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{formatCurrency(annualPremium)}</div>
          )}
          {editing && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>{t('screens.policiesScreen.detail.colStatus', { defaultValue: 'Status' })}</div>
              <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={{ padding: '4px 8px', border: `1px solid ${S.borderLight}`, borderRadius: 4, fontSize: 13, width: '100%' }}>
                {EDITABLE_STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_LABEL_KEYS[s] ? t(STATUS_LABEL_KEYS[s]) : s}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>{t('screens.policiesScreen.detail.colBroker')}</div>
          {/* D-23 retest: previous fix pushed to /brokers (list). Anjani
              expected drill-in to the specific broker. BrokersScreen
              uses local selection (no /:id route); pass ?open=<id> so
              useBrokersFunctions auto-selects on mount. Same deep-link
              pattern the offers list uses (D-11). */}
          {(() => {
            const rawBroker = policy.broker;
            const brokerId = rawBroker && typeof rawBroker === 'object' ? rawBroker._id : rawBroker;
            return policy.brokerName && policy.brokerName !== '—' ? (
              <button
                type='button'
                onClick={() => history.push(brokerId ? `/brokers?open=${brokerId}` : '/brokers')}
                style={{ fontSize: 14, fontWeight: 600, color: S.burgundy, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
              >
                {policy.brokerName}
              </button>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 600, color: S.textPrimary }}>{policy.brokerName || '—'}</div>
            );
          })()}
        </div>
      </div>
    </header>
  );
};

export default Header;
